import {
	BASE_PRECISION,
	BN,
	VelocityClient,
	MarketType,
	OrderStatus,
	User,
	ZERO,
} from '@velocity-exchange/sdk';
import {
	ENUM_UTILS,
	Serializer,
	UISerializableOrder,
} from '@velocity-exchange/common';
import { OrdersResult } from './types';

export function getAllNonZeroOrders(user: User): UISerializableOrder[] {
	const userAccount = user.getUserAccountOrThrow();
	return userAccount.orders
		.filter((order) => !order.baseAssetAmount.isZero())
		.map(Serializer.Serialize.Order)
		.map(Serializer.Deserialize.UIOrder);
}

export function computeOrders(
	user: User,
	driftClient: VelocityClient | undefined
): OrdersResult {
	const nonZeroOrders = getAllNonZeroOrders(user);

	const openOrders = nonZeroOrders
		.filter((order) => {
			return (
				!ENUM_UTILS.match(order.status, OrderStatus.CANCELED) &&
				!ENUM_UTILS.match(order.status, OrderStatus.FILLED)
			);
		})
		.sort((orderA, orderB) => orderB.orderId - orderA.orderId);

	const userPositions = user.getUserAccountOrThrow().perpPositions;

	const quoteInOpenOrders = openOrders.reduce((prev, current) => {
		const priceToUse = current.price?.gtZero()
			? current.price.val
			: ENUM_UTILS.match(current.marketType, MarketType.PERP)
				? driftClient?.getOracleDataForPerpMarket(current.marketIndex)?.price
				: driftClient?.getOracleDataForSpotMarket(current.marketIndex)?.price;

		const currentPosition = userPositions.find(
			(pos) => pos.marketIndex === current.marketIndex
		);
		const currentPositionBaseAmount = currentPosition?.baseAssetAmount ?? ZERO;

		const baseAmountToUse = current.reduceOnly
			? (() => {
					const orderBase = current.baseAssetAmount?.val;
					const positionBase = currentPositionBaseAmount;
					const clamped = BN.min(orderBase.abs(), positionBase.abs());
					return orderBase.isNeg() ? clamped.neg() : clamped;
				})()
			: current.baseAssetAmount?.val;

		return prev.add(
			(
				baseAmountToUse
					?.abs()
					.sub(current?.baseAssetAmountFilled?.val?.abs() ?? ZERO) ?? ZERO
			)
				.mul(priceToUse ?? ZERO)
				.div(BASE_PRECISION)
		);
	}, ZERO);

	return {
		openOrders,
		quoteInOpenOrders: quoteInOpenOrders.abs(),
	};
}
